import { useSelector } from 'react-redux'
import {
  getCashRoundingMinimumCurrencyInCents,
  getCashRoundingEnabled,
  getUseNormalTaxRounding,
  getUseTaxClasses,
} from '../../selectors/menus'
import { getSelectedOrder } from '../../selectors/order'
import { getCartItemsFromOrder, getTaxByItem, getTaxRate } from '../../selectors/cart'
import { getMenuServiceCharge } from '../../selectors/items'
import {
  calculateIndividualTaxAmount,
  calculateServiceCharge,
  calculateServiceFeeTaxRules,
  calculateSubtotal,
  calculateTax,
} from '../../utils/totalUtils'
import { getTaxClasses, getTaxRules } from '../../VNTaxes/Selectors'
import { roundToNearestInterval } from '../../utils/orderUtils'
import PaymentTypes from '../../utils/paymentTypes'

export const useRefundCalculator = () => {
  const order = useSelector(getSelectedOrder)
  const currentCart = useSelector((state) => getCartItemsFromOrder(state, order.uuid))
  const useNormalTaxRounding = useSelector(getUseNormalTaxRounding)
  const taxRate = useSelector(getTaxRate)
  const taxByItem = useSelector(getTaxByItem)
  const serviceCharge = useSelector(getMenuServiceCharge)
  const taxClasses = useSelector(getTaxClasses)
  const taxRules = useSelector(getTaxRules)
  const useTaxClasses = useSelector(getUseTaxClasses)
  const cashRoundingMinimumCurrencyInCents = useSelector(getCashRoundingMinimumCurrencyInCents)
  const cashRoundingEnabled = useSelector(getCashRoundingEnabled)

  const calculateItemsRefund = (refundItemIndexes = [], refundModifiers = {}, isEntireOrder = false) => {
    const refundItems = currentCart.filter((_, index) => refundItemIndexes.includes(index))
    const isCharityAmountChecked = refundItems.some((item) => item.type === 'DonationLineItem')
    const charityItem = refundItems.find((item) => item.type === 'DonationLineItem')

    if (!refundItems.length && !Object.keys(refundModifiers).length) {
      return { calculatedRefundInCents: 0, isRefundCashRounded: false }
    }

    const modifiersAmountInCents = Object.values(refundModifiers).reduce((acc, modifer) => {
      if (refundItemIndexes.includes(modifer.parentItemIndex)) {
        return acc
      }

      return acc + modifer.defaultPriceInCents
    }, 0)

    const subtotalAmountInCents = calculateSubtotal(refundItems, true)

    const refundRemainingOrder = currentCart.length === refundItems.length
    const serviceChargeAmountInCents = calculateServiceCharge(
      serviceCharge,
      subtotalAmountInCents,
      refundRemainingOrder,
      isCharityAmountChecked,
      charityItem?.defaultPriceInCents,
    )
    const tipAmountInCents = refundRemainingOrder ? order.tipAmountInCents : 0

    let serviceFeeTaxAmountInCents = 0
    const serviceFee = {
      taxClassUuid: serviceCharge?.taxClassUuid,
      total: serviceChargeAmountInCents || 0,
    }

    if (useTaxClasses) {
      const calculateServiceFeeTaxRulesResult = calculateServiceFeeTaxRules(
        {}, //no need to send breakdown
        serviceFee,
        useNormalTaxRounding,
        taxClasses,
        taxRules,
        order?.taxExempt || false,
      )
      serviceFeeTaxAmountInCents = calculateServiceFeeTaxRulesResult.serviceFeeTaxAmountInCents
    } else {
      serviceFeeTaxAmountInCents = calculateIndividualTaxAmount(
        taxRate,
        serviceChargeAmountInCents,
        serviceCharge?.taxable,
        useNormalTaxRounding,
      )
    }

    const calculatedTax = calculateTax(
      refundItems,
      taxRate,
      taxByItem,
      useNormalTaxRounding,
      useTaxClasses,
      taxClasses,
      taxRules,
      order?.taxExempt || false,
    )
    const taxAmountInCents = calculatedTax.taxAmountInCents + serviceFeeTaxAmountInCents

    const calculatedRefundInCents =
      modifiersAmountInCents + subtotalAmountInCents + taxAmountInCents + serviceChargeAmountInCents + tipAmountInCents

    let cashRoundingInCents = 0
    //refunds will only be calculated with cash rounding if the feature is enabled, order has only one payment method, and the payment is cash
    const hasOnlyOnePayment = order?.payments?.length === 1
    const isSinglePaymentCash = hasOnlyOnePayment && PaymentTypes.isCash(order.payments[0]?.paymentType)

    if (cashRoundingEnabled && isSinglePaymentCash) {
      const roundedCalculatedRefundInCents = roundToNearestInterval({
        amountInCents: calculatedRefundInCents,
        cashRoundingMinimumCurrencyInCents,
      })
      cashRoundingInCents = roundedCalculatedRefundInCents - calculatedRefundInCents
    }

    // In the case of a full refund, we want to ensure that we refund the entire amount of the order
    if (cashRoundingEnabled && isEntireOrder) {
      //we will always send isRefundCashRounded as false for entire order refunds with cash rounding because we assume
      //that the the cash payment was rounded at the time of purchase, so we want to refund the exact amount
      //that was paid which is the order total.
      return { calculatedRefundInCents: order.amountInCents, isRefundCashRounded: false }
    }

    //Never refund more than the amountInCents this will prevent refunding more than what the user originally payed.
    if (calculatedRefundInCents > order.amountInCents) {
      return { calculatedRefundInCents: order.amountInCents, isRefundCashRounded: cashRoundingInCents !== 0 }
    }

    return {
      calculatedRefundInCents: calculatedRefundInCents + cashRoundingInCents,
      isRefundCashRounded: cashRoundingInCents !== 0,
    }
  }

  return {
    calculateItemsRefund,
  }
}
